import React, { memo } from 'react'
import styled from 'styled-components/native'

import { AccessibilityRole } from 'libs/accessibilityRole/accessibilityRole'
import { ColorsType } from 'theme/types'
import { Typo, getSpacing } from 'ui/theme'

enum BarHeight {
  large = 6,
  normal = 4.5,
  small = 2,
  smaller = 1,
}

interface CreditProgressBarProps {
  progress: number
  color: ColorsType
  height?: 'large' | 'normal' | 'small' | 'smaller'
  width?: string
  innerText?
}

const MINIMUM_PROGRESS_BAR_SIZE = 0.02
const MINIMUM_PROGRESS_BAR_SIZE_SM = 0.07
const MINIMUM_PROGRESS_BAR_SIZE_MD = 0.03

const CreditProgressBarComponent: React.FC<CreditProgressBarProps> = ({
  color,
  progress,
  height = 'large',
  width = '100%',
  innerText,
}) => {
  const progressString = `${Math.round(progress * 100)}`
  return (
    <Container
      width={width}
      accessibilityLabel={`Barre de progression\u00a0: ${progressString}%`}
      accessibilityRole={AccessibilityRole.PROGRESSBAR}
      accessible>
      <ProgressBarContainer height={height}>
        {height === 'large' ? <BaseShadowGradient /> : null}
        <LinearGradientBar progress={progress} color={color} testID="progress-bar">
          <StyledBodyAccent>{innerText}</StyledBodyAccent>
        </LinearGradientBar>
      </ProgressBarContainer>
    </Container>
  )
}

export const CreditProgressBar = memo(
  styled(CreditProgressBarComponent).attrs<{
    color?: ColorsType
  }>(({ theme, color }) => ({
    color: color ?? theme.designSystem.color.background.brandPrimary,
  }))``
)
type CreditProgressPropsWithoutWidth = Omit<CreditProgressBarProps, 'width'>

const LinearGradientBar = styled.View.attrs<CreditProgressPropsWithoutWidth>(({ color }) => ({
  color,
}))<CreditProgressPropsWithoutWidth>(({ theme, progress, color }) => {
  let flex
  if (progress === 0) {
    flex = progress
  } else if (theme.appContentWidth < theme.breakpoints.sm) {
    flex = Math.max(progress, MINIMUM_PROGRESS_BAR_SIZE_SM)
  } else if (theme.appContentWidth < theme.breakpoints.md) {
    flex = Math.max(progress, MINIMUM_PROGRESS_BAR_SIZE_MD)
  } else {
    flex = Math.max(progress, MINIMUM_PROGRESS_BAR_SIZE)
  }

  return {
    flex,
    backgroundColor: color,
    borderRadius: theme.designSystem.size.borderRadius.pill,
  }
})

const BaseShadowGradient = styled.View(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  borderRadius: theme.designSystem.size.borderRadius.pill,
  backgroundColor: theme.designSystem.color.background.disabled,
}))

const Container = styled.View<{ width: string }>`
  width: ${({ width }) => width};
  flex-direction: row;
  align-items: center;
  justify-content: center;
`

const ProgressBarContainer = styled.View<Pick<CreditProgressBarProps, 'height'>>(
  ({ theme, height }) => ({
    flexDirection: 'row',
    borderRadius: theme.designSystem.size.borderRadius.pill,
    height: getSpacing(height ? BarHeight[height] : BarHeight.large),
    width: '100%',
    zIndex: theme.zIndex.progressbar,
    backgroundColor:
      height === 'small'
        ? theme.designSystem.color.background.subtle
        : theme.designSystem.color.background.default,
  })
)

const StyledBodyAccent = styled(Typo.BodyAccent)(({ theme }) => ({
  textAlign: 'center',
  color: theme.designSystem.color.text.inverted,
}))
